import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Slider,
  FormControlLabel,
  Switch,
  Breadcrumbs,
  Link as MuiLink,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Save as SaveIcon, 
  Delete as DeleteIcon, 
  ArrowBack as ArrowBackIcon,
  Publish as PublishIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { 
  getExpoRelease, 
  updateExpoRelease, 
  deleteExpoRelease,
  createExpoRelease,
  getExpoChannels,
  publishExpoRelease,
  rollbackExpoRelease,
} from '../../services/expoUpdates';
import type { ExpoChannel, ExpoRelease } from '../../models/expo';

const ExpoReleaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const isNew = id === 'create';
  const navigate = useNavigate();

  // Get channel ID from query params if it exists
  const queryParams = new URLSearchParams(location.search);
  const preselectedChannelId = queryParams.get('channel');

  const [release, setRelease] = useState<Partial<ExpoRelease>>({
    channel: preselectedChannelId ? parseInt(preselectedChannelId) : undefined,
    version: '',
    runtime_version: '',
    platform: 'all',
    changelog: '',
    is_mandatory: false,
    rollout_percentage: 100,
    metadata: {},
    manifest: {}
  });
  const [channels, setChannels] = useState<ExpoChannel[]>([]);
  const [manifestText, setManifestText] = useState<string>('{}');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [actionDialogOpen, setActionDialogOpen] = useState<boolean>(false);
  const [actionType, setActionType] = useState<'publish' | 'rollback'>('publish');

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const channelsData = await getExpoChannels();
        setChannels(channelsData);
        
        if (!isNew) {
          const releaseId = parseInt(id!);
          const releaseData = await getExpoRelease(releaseId);
          setRelease(releaseData);
          
          // Initialize manifest text from the release data
          if (releaseData.manifest) {
            setManifestText(JSON.stringify(releaseData.manifest, null, 2));
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id, isNew]);

  // Handle form changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRelease((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setRelease((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setRelease((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSliderChange = (_e: Event, newValue: number | number[]) => {
    setRelease((prev) => ({
      ...prev,
      rollout_percentage: newValue as number,
    }));
  };

  const handleManifestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManifestText(e.target.value);
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    // Parse the manifest JSON
    let manifestObj = {};
    try {
      if (manifestText) {
        manifestObj = JSON.parse(manifestText);
      }
    } catch (err) {
      setError('Manifest JSON is invalid');
      setSaving(false);
      return;
    }
    
    try {
      const releaseWithManifest = {
        ...release,
        manifest: manifestObj
      };
      
      if (isNew) {
        await createExpoRelease(releaseWithManifest);
      } else {
        await updateExpoRelease(parseInt(id!), releaseWithManifest);
      }
      navigate('/expo-updates');
    } catch (err) {
      console.error('Failed to save release:', err);
      setError('Failed to save release. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle release delete
  const handleDelete = async () => {
    setDeleteDialogOpen(false);
    setSaving(true);
    setError(null);
    
    try {
      await deleteExpoRelease(parseInt(id!));
      navigate('/expo-updates');
    } catch (err) {
      console.error('Failed to delete release:', err);
      setError('Failed to delete release. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle publish/rollback
  const openActionDialog = (action: 'publish' | 'rollback') => {
    setActionType(action);
    setActionDialogOpen(true);
  };

  const handleActionConfirm = async () => {
    setActionDialogOpen(false);
    setSaving(true);
    setError(null);
    
    try {
      if (actionType === 'publish') {
        await publishExpoRelease(parseInt(id!));
      } else {
        await rollbackExpoRelease(parseInt(id!));
      }
      // Refresh the release data
      const releaseData = await getExpoRelease(parseInt(id!));
      setRelease(releaseData);
    } catch (err) {
      console.error(`Failed to ${actionType} release:`, err);
      setError(`Failed to ${actionType} release. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  // Get the selected channel name
  const getSelectedChannelName = () => {
    if (release.channel) {
      const selectedChannel = channels.find(c => c.id === release.channel);
      return selectedChannel?.name || '';
    }
    return '';
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink component={Link} to="/">
          Home
        </MuiLink>
        <MuiLink component={Link} to="/expo-updates">
          Expo Updates
        </MuiLink>
        <Typography color="text.primary">
          {isNew ? 'Create Release' : `Release: ${release.version}`}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {isNew ? 'Create New Release' : `Edit Release: ${release.version}`}
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/expo-updates"
            sx={{ mr: 2 }}
          >
            Back
          </Button>

          {!isNew && (
            <>
              {release.status === 'draft' && (
                <Button 
                  variant="outlined" 
                  color="success" 
                  startIcon={<PublishIcon />}
                  onClick={() => openActionDialog('publish')}
                  sx={{ mr: 2 }}
                >
                  Publish
                </Button>
              )}
              {release.status === 'published' && (
                <Button 
                  variant="outlined" 
                  color="warning" 
                  startIcon={<UndoIcon />}
                  onClick={() => openActionDialog('rollback')}
                  sx={{ mr: 2 }}
                >
                  Rollback
                </Button>
              )}
              <Button 
                variant="outlined" 
                color="error" 
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ mr: 2 }}
                disabled={release.status === 'published'}
              >
                Delete
              </Button>
            </>
          )}
          
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saving || (!isNew && release.status === 'published')}
          >
            Save
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {!isNew && release.status === 'published' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This release is published and cannot be edited. You can rollback if needed.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit}>
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="channel-label">Channel</InputLabel>
                  <Select
                    labelId="channel-label"
                    id="channel"
                    name="channel"
                    value={release.channel || ''}
                    label="Channel"
                    onChange={handleSelectChange}
                    disabled={!isNew && release.status === 'published'}
                  >
                    {channels.map((channel) => (
                      <MenuItem key={channel.id} value={channel.id}>
                        {channel.name} ({channel.app_name})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select the update channel</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="platform-label">Platform</InputLabel>
                  <Select
                    labelId="platform-label"
                    id="platform"
                    name="platform"
                    value={release.platform || 'all'}
                    label="Platform"
                    onChange={handleSelectChange}
                    disabled={!isNew && release.status === 'published'}
                  >
                    <MenuItem value="all">All Platforms</MenuItem>
                    <MenuItem value="ios">iOS</MenuItem>
                    <MenuItem value="android">Android</MenuItem>
                  </Select>
                  <FormHelperText>Target platform for this update</FormHelperText>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="version"
                  name="version"
                  label="Version"
                  value={release.version || ''}
                  onChange={handleInputChange}
                  disabled={!isNew && release.status === 'published'}
                  helperText="Version identifier for this release (e.g., 1.0.0)"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="runtime_version"
                  name="runtime_version"
                  label="Runtime Version"
                  value={release.runtime_version || ''}
                  onChange={handleInputChange}
                  disabled={!isNew && release.status === 'published'}
                  helperText="Runtime version to target with this update"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Manifest JSON
                </Typography>
                {!isNew && release.status === 'published' && release.manifest && (
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This release is published. View-only manifest shown below.
                    </Alert>
                    <TextField
                      fullWidth
                      multiline
                      rows={12}
                      value={manifestText}
                      disabled
                      InputProps={{
                        style: { fontFamily: 'monospace' }
                      }}
                    />
                  </Box>
                )}
                
                {(isNew || release.status !== 'published') && (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      The manifest defines the update content according to the Expo Updates protocol. 
                      Key fields are: runtimeVersion, launchAsset (entry point), and assets (resources).
                    </Alert>
                    <TextField
                      fullWidth
                      multiline
                      rows={12}
                      id="manifest"
                      name="manifest"
                      label="Manifest"
                      value={manifestText}
                      onChange={handleManifestChange}
                      disabled={!isNew && release.status === 'published'}
                      InputProps={{
                        style: { fontFamily: 'monospace' }
                      }}
                      helperText="Edit the JSON manifest directly according to the Expo Updates protocol"
                    />
                  </>
                )}
              </Grid>

              {/* Display UUID if available */}
              {!isNew && release.uuid && (
                <Grid item xs={12}>
                  <TextField
                    label="UUID"
                    value={release.uuid || ''}
                    fullWidth
                    disabled
                    helperText="Unique identifier used in the Expo Updates protocol"
                  />
                </Grid>
              )}
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Release Options
            </Typography>
            <Divider sx={{ mb: 3 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography gutterBottom>Rollout Percentage: {release.rollout_percentage}%</Typography>
                <Slider
                  name="rollout_percentage"
                  value={release.rollout_percentage || 100}
                  onChange={handleSliderChange}
                  disabled={!isNew && release.status === 'published'}
                  valueLabelDisplay="auto"
                  step={10}
                  marks
                  min={10}
                  max={100}
                  sx={{ width: '100%', mt: 2 }}
                />
                <FormHelperText>
                  Percentage of devices that will receive this update (phased rollout)
                </FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      name="is_mandatory"
                      checked={release.is_mandatory || false}
                      onChange={handleSwitchChange}
                      disabled={!isNew && release.status === 'published'}
                    />
                  }
                  label="Mandatory Update"
                />
                <FormHelperText>
                  If enabled, users must update to continue using the app
                </FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  id="changelog"
                  name="changelog"
                  label="Changelog"
                  value={release.changelog || ''}
                  onChange={handleInputChange}
                  disabled={!isNew && release.status === 'published'}
                  helperText="Release notes or description of changes (optional)"
                />
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Release</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this release? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish/Rollback Confirmation Dialog */}
      <Dialog
        open={actionDialogOpen}
        onClose={() => setActionDialogOpen(false)}
      >
        <DialogTitle>
          {actionType === 'publish' ? 'Publish Release' : 'Rollback Release'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {actionType === 'publish' ? (
              `Are you sure you want to publish this release to the ${getSelectedChannelName()} channel? 
              This will make it available to ${release.rollout_percentage}% of users.`
            ) : (
              'Are you sure you want to rollback this release? This will prevent new users from receiving this update.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleActionConfirm} 
            color={actionType === 'publish' ? 'success' : 'warning'} 
            autoFocus
          >
            {actionType === 'publish' ? 'Publish' : 'Rollback'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpoReleaseDetail;
